import React from 'react';
import ReactDOM from 'react-dom';
import styleHelper from './../helpers/style';


class AutoplayingVideo extends React.Component {
  constructor(props) {
    super(props);
    /**
     * Used by React as the component name
     */
    this.displayName = 'AutoplayingVideo';


    /**
     * The type of video (native, YouTube, or Vimeo)
     */
    this.videoType = 'native';


    /**
     * Timeout to hide controls
     */
    this.hideControlsTimeout = 0;


    /**
     * Time to wait before hiding controls
     */
    this.hideControlsDelay = 1000;


    /**
     * If the YouTube player has been initialized
     */
    this.youtubePlayerInitialized = false;


    /**
     * A reference to the player object
     */
    this.player = null;


    /**
     * If the user's mouse is over top of the play/pause button
     */
    this.overPlayPauseButton = false;


    /**
     * The video duration
     */
    this.duration = 0;


    /**
     * Will be used as a setInterval to check the status of a YouTube video
     */
    this.checkStatusInterval = 0;


    /**
     * The previous windowSize
     */
    this.previousWindowSize = {
      width: -1,
      height: -1,
    };


    /**
     * Set initial state
     */
    this.state = {
      playing: false,
      paused: false,
      stopped: true,
      videoStyle: {
        width: 0,
        height: 0,
        marginTop: 0,
        marginLeft: 0,
      },
    };


    // bind functions to component
    this.renderVideo = this.renderVideo.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.renderNativeVideo = this.renderNativeVideo.bind(this);
    this.setVideoStyle = this.setVideoStyle.bind(this);
    this.togglePlayPause = this.togglePlayPause.bind(this);
    this.setPlayer = this.setPlayer.bind(this);
    this.playVideo = this.playVideo.bind(this);
    this.pauseVideo = this.pauseVideo.bind(this);
    this.stopVideo = this.stopVideo.bind(this);
    this.onVimeoPlayerEnd = this.onVimeoPlayerEnd.bind(this);
    this.onYouTubePlayerReady = this.onYouTubePlayerReady.bind(this);
    this.onYouTubePlayerStateChange = this.onYouTubePlayerStateChange.bind(this);
    this.onNativePlayerEnd = this.onNativePlayerEnd.bind(this);
    this.resetPlayer = this.resetPlayer.bind(this);
  }


  /**
   * Called after component mounts to DOM
   * @returns {undefined} undefined
   */
  componentDidMount() {
    this.node = ReactDOM.findDOMNode(this);
    this.container = this.node.parentNode;
    this.setVideoStyle();

    const youtubeApiSrcTag = document.createElement('script');
    youtubeApiSrcTag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(youtubeApiSrcTag, firstScriptTag);

    const vimeoApiSrcTag = document.createElement('script');
    vimeoApiSrcTag.src = '//thelab.bleacherreport.com/scripts/froogaloop.js';
    firstScriptTag.parentNode.insertBefore(vimeoApiSrcTag, firstScriptTag);


    if (!this.props.touch) {
      this.onScroll();
    }
  }


  /**
   * Called when component updates
   * @returns {undefined} undefined
   */
  componentDidUpdate(prevProps) {
    if (this.props.windowSize &&
      ((this.props.windowSize.width !== prevProps.windowSize.width) ||
      (this.props.windowSize.height !== prevProps.windowSize.height))) {
      this.onChange();
    }

    if (this.props.scrollTop &&
      this.props.scrollTop !== prevProps.scrollTop) {
      this.onScroll();
    }
  }


  /**
   * Renders component
   * @returns {Object} React element
   */
  render() {
    const fullBleedClass = this.props.fullBleed ? ' full-bleed' : '';
    const playingClass = this.state.playing ? ' playing' : '';
    const pausedClass = this.state.paused ? ' paused' : '';
    const stoppedClass = this.state.stopped ? ' stopped' : '';
    const sectionClass = ' section-' + this.props.iteration + '-' +
      this.props.id;
    const showControlsClass = this.state.showControls ?
      ' show-controls' : '';
    const prevSectionClass = ' prev-section-type-' + this.props.prevSectionType;
    const className = 'component-autoplaying-video' +
      sectionClass +
      fullBleedClass +
      playingClass +
      pausedClass +
      stoppedClass +
      showControlsClass +
      prevSectionClass;
    const video = this.renderVideo();
    let wrapperBackgroundStyle = {};
    if (this.videoType === 'native') {
      wrapperBackgroundStyle = {
        backgroundImage: 'url(' + this.props.posterImageUrl + ')',
      };
    }

    return (
      <section className={className} style={this.props.style}>
        <div className='video-wrapper' style={wrapperBackgroundStyle}>
          {video}
        </div>
      </section>
    );
  }


  /**
   * Renders the video element
   * @returns {Object} React element
   */
  renderVideo() {
    let videoElement;

    if (!this.props.touch) {
      let playerElement;
      const videoStyle = this.state.videoStyle;
      let loop = true;
      if (this.props.loop === false) {
        loop = false;
      }
      const loopParam = this.props.loop ? 1 : 0;
      const style = {
        marginTop: videoStyle.marginTop,
        marginLeft: videoStyle.marginLeft,
      };
      const youTubeRegex =
        /(?:https?:\/\/)?(?:youtu\.be\/|(?:www\.)?youtube\.com\/watch(?:\.php)?\?.*v=)([a-zA-Z0-9\-_]+)/;

      if (youTubeRegex.test(this.props.videoUrl)) {
        this.videoType = 'YouTube';

        let videoId = this.props.videoUrl.split('v=')[1];
        const ampersandPosition = videoId.indexOf('&');
        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }

        playerElement = (
          <div className='video-element-wrapper'>
            <div className='embed-content' id={this.props.elementId} />
          </div>
        );

        if (!window.YT && !this.youtubePlayerInitialized && videoStyle.width && videoStyle.height) {
          this.youtubePlayerInitialized = true;

          this.renderYouTubeVideo(
            videoId,
            videoStyle,
            loopParam
          );
        }
      } else if (this.props.videoUrl.indexOf('vimeo.com') !== -1) {
        this.videoType = 'Vimeo';
        const vimeoIdRegex =
          /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
        const vimeoId = this.props.videoUrl.match(vimeoIdRegex)[3];

        playerElement = this.renderVimeoVideo(
          vimeoId,
          videoStyle,
          loopParam
        );
      } else {
        const posterImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///' +
          'yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

        playerElement = this.renderNativeVideo(
          this.props.videoUrl,
          videoStyle,
          posterImage,
          loop
        );
      }

      videoElement = (
        <div style={style} className='embed-wrapper'>
          {playerElement}
          <div className='embed-block' />
        </div>
      );
    }

    return videoElement;
  }


  /**
   * Renders a YouTube video
   * @param {String} videoId - The id of the YouTube video
   * @param {Object} style - The style of the video
   * @param {Boolean} loop - If the video should loop
   * @returns {ReactElement} A video element
   */
  renderYouTubeVideo(videoId, style, loop) {
    const inst = this;

    if (typeof window !== 'undefined' && window.YouTubeIframeLoader) {
      window.YouTubeIframeLoader.load((YT) => {
        inst.player = new YT.Player(inst.props.elementId, {
          height: style.height,
          width: style.width,
          videoId,
          playerVars: { loop, controls: 0 },
          events: {
            onReady: inst.onYouTubePlayerReady,
            onStateChange: inst.onYouTubePlayerStateChange,
          },
        });
      });
    }
  }


  /**
   * Renders a native video player
   * @param {String} videoUrl - The url of the video
   * @param {Object} style - The style of the video
   * @param {String} posterImageUrl - The url of the poster image
   * @param {Boolean} loop - If the video should loop
   * @returns {ReactElement} A video element
   */
  renderNativeVideo(videoUrl, style, posterImageUrl, loop) {
    return (
      <div className='video-element-wrapper'>
        <video
          height={style.height}
          id={this.props.elementId}
          loop={loop}
          poster={posterImageUrl}
          preload='none'
          ref='nativePlayerElement'
          width={style.width}
        >
          <source src={videoUrl} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
        <div
          className='embed-block'
          onClick={this.togglePlayPause}
        />
      </div>
    );
  }


  /**
   * Renders a Vimeo video
   * @param {String} videoId - The id of the Vimeo video
   * @param {Object} style - The style of the video
   * @param {Boolean} loop - If the video should loop
   * @returns {ReactElement} A video element
   */
  renderVimeoVideo(videoId, style, loop) {
    return (
      <div className='video-element-wrapper'>
        <iframe
          id={this.props.elementId}
          src={'https://player.vimeo.com/video/' + videoId +
            '?api=1&enablejsapi=1&loop=' + loop +
            '&player_id=' + this.props.elementId}
          width={style.width}
          height={style.height}
          frameBorder='0'
          webkitAllowFullScreen
          mozAllowFullScreen
          allowFullScreen
        >
        </iframe>
        <div className='embed-block' onClick={this.togglePlayPause} />
      </div>
    );
  }


  /**
   * Sets the video style
   * @returns {undefined} undefined
   */
  setVideoStyle() {
    let ratio = 9 / 16;

    if (this.props.videoWidth && this.props.videoHeight) {
      ratio = this.props.videoHeight / this.props.videoWidth;
    }

    if (!this.props.fullBleed) {
      const width = this.container.offsetWidth;

      this.setState({
        videoStyle: {
          width,
          height: width * ratio,
          marginTop: 0,
          marginLeft: 0,
        },
      });
    } else {
      this.setState({
        videoStyle: styleHelper.getSizeToCover(
          this.props.videoWidth,
          this.props.videoHeight,
          this.container.offsetWidth,
          this.container.offsetHeight
        ),
      });
    }
  }


  /**
   * Called when window is scrolled
   * @returns {undefined} undefined
   */
  onScroll() {
    const rect = this.node.getBoundingClientRect();

    if (rect.top <= (window.innerHeight + this.props.scrollPadding) &&
      rect.bottom >= -this.props.scrollPadding) {
      if (!this.state.playing) {
        this.playVideo();
      }
    } else {
      if (this.state.playing) {
        this.stopVideo();
      }
    }
  }


  /**
   * Toggles the playing/pausing the video
   * @returns {undefined} undefined
   */
  togglePlayPause() {
    if (this.state.paused || this.state.stopped) {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
  }


  /**
   * Sets a variable to hold the player
   * @returns {undefined} undefined
   */
  setPlayer() {
    const inst = this;
    const videoElement = this.refs.nativePlayerElement;

    if (!this.player) {
      if (this.videoType === 'Vimeo') {
        this.player = window.$f(videoElement);
        this.player.addEvent('ready', () => {
          inst.player.addEvent('finish', inst.onVimeoPlayerEnd);
          inst.player.api('getDuration', (duration) => {
            inst.duration = duration;
          });
        });
      } else if (this.videoType === 'YouTube') {
        // set in renderVideo()
      } else if (this.videoType === 'native') {
        this.player = videoElement;
        this.player.addEventListener('ended', this.onNativePlayerEnd);
      }
    }
  }


  /**
   * Plays the video
   * @returns {undefined} undefined
   */
  playVideo() {
    if (!this.props.touch) {
      this.setPlayer();

      if (this.videoType === 'Vimeo') {
        this.player.api('play');
      } else if (this.videoType === 'YouTube') {
        clearInterval(this.checkStatusInterval);
        if (this && this.player) {
          this.player.playVideo();
        }
      } else if (this.videoType === 'native') {
        if (this.player.play) {
          this.player.play();
        }
      }

      this.setState({
        showControls: false,
        playing: true,
        paused: false,
        stopped: false,
      });
    }
  }


  /**
   * Pauses the video
   * @returns {undefined} undefined
   */
  pauseVideo() {
    if (!this.props.touch) {
      this.setPlayer();

      if (this.videoType === 'Vimeo') {
        this.player.api('pause');
      } else if (this.videoType === 'YouTube') {
        clearInterval(this.checkStatusInterval);
        this.player.pauseVideo();
      } else if (this.videoType === 'native') {
        this.player.pause();
      }

      this.setState({
        showControls: true,
        playing: true,
        paused: true,
        stopped: false,
      });
    }
  }


  /**
   * Stops the video
   * @returns {undefined} undefined
   */
  stopVideo() {
    if (!this.props.touch) {
      this.setPlayer();

      if (this.videoType === 'Vimeo') {
        this.player.api('stop');
      } else if (this.videoType === 'YouTube') {
        clearInterval(this.checkStatusInterval);
        this.player.stopVideo();
      } else if (this.videoType === 'native') {
        this.player.pause();
        this.player.currentTime = 0;
      }

      this.setState({
        showControls: true,
        playing: false,
        paused: false,
        stopped: true,
      });
    }
  }


  /**
   * Called when a 'CHANGE' event is emitted from the AutoplayingVideoStore
   * @returns {undefined} undefined
   */
  onChange() {
    if (!this.props.touch) {
      this.setVideoStyle();
    }
  }


  /**
   * Called while the video is playing
   * @returns {undefined} undefined
   */
  onVimeoPlayerEnd() {
    if (this.props.loop === false) {
      this.resetPlayer();
    }
  }


  /**
   * Called when the YouTube player is ready
   * @returns {undefined} undefined
   */
  onYouTubePlayerReady() {
    if (!this.props.touch) {
      this.onScroll();
    }
  }


  /**
   * Called when the YouTube player is playing
   * @param {Object} e - An event dispatched from the YouTube player
   * @returns {undefined} undefined
   */
  onYouTubePlayerStateChange(e) {
    if (e.data === 0) {
      if (this.props.loop === false) {
        clearInterval(this.checkStatusInterval);
        this.resetPlayer();
      } else {
        this.playVideo();
      }
    }
  }


  /**
   * Called when the native player video ends
   * @returns {undefined} undefined
   */
  onNativePlayerEnd() {
    if (this.props.loop === false) {
      this.resetPlayer();
    }
  }


  /**
   * Resets the state
   * @returns {undefined} undefined
   */
  resetPlayer() {
    this.setState({
      playing: false,
      paused: false,
      stopped: true,
    });
  }
}


/**
 * Set default props
 */
AutoplayingVideo.defaultProps = {
  scrollPadding: 200,
};


/**
 * Expected propTypes
 * @prop {String} elementId - A unique id for the video element. If not
 * provided, a random id will be generated
 * @prop {Boolean} fullBleed - If the video should be sized full-bleed within
 * its container
 * @prop {String} id - A unique id for the video element
 * @prop {Number} iteration - The iteration of the element
 * @prop {Boolean} loop - If the video should loop. Defaults to true
 * @prop {String} posterImageUrl - The url of the poster image, which will also
 * be used as a fallback for mobile
 * @prop {String} prevSectionType - The type of the previous section
 * @prop {Number} scrollPadding - A pixel amount before the video reaches
 * the bottom or top of the window, where the video will start playing
 * @prop {Number} scrollTop - The amount, in pixels, the user has scrolled in
 * the entire article
 * @prop {Number} style - A style object to apply to the component
 * @prop {Boolean} touch - If browser supports touch events
 * @prop {String} videoUrl - The url of the video
 * @prop {Number} videoWidth - The width of the video, in pixels
 * @prop {Number} videoHeight - The height of the video, in pixels
 * @prop {Object} windowSize - An object holding the width
 * and size of the window
 */
AutoplayingVideo.propTypes = {
  elementId: React.PropTypes.string.isRequired,
  fullBleed: React.PropTypes.bool,
  id: React.PropTypes.number.isRequired,
  iteration: React.PropTypes.number,
  loop: React.PropTypes.bool,
  posterImageUrl: React.PropTypes.string,
  prevSectionType: React.PropTypes.string,
  scrollPadding: React.PropTypes.number,
  scrollTop: React.PropTypes.number,
  style: React.PropTypes.object,
  touch: React.PropTypes.bool,
  videoUrl: React.PropTypes.string.isRequired,
  videoWidth: React.PropTypes.number,
  videoHeight: React.PropTypes.number,
  windowSize: React.PropTypes.object,
};


export default AutoplayingVideo;



/** WEBPACK FOOTER **
 ** ./assets/js/src/components/AutoplayingVideo.jsx
 **/